import {
  ModeIds,
  VARIANT_CHESS,
  VARIANT_CHESS960,
  TimeControl,
} from "@chesshotel/constants";
import useTranslation from "next-translate/useTranslation";
import { FaBolt } from "react-icons/fa";
import { FaDragon } from "react-icons/fa";
import { FaChevronUp } from "react-icons/fa";
import { FaChevronDown } from "react-icons/fa";
import { FaPaperPlane } from "react-icons/fa";
import { FaClock } from "react-icons/fa";
import { showCustomGamePopup, useLobby } from "../../redux/lobby";
import { BulletIcon } from "../../icons/BulletIcon";
import GameButtonDropdown from "./GameButtonDropdown";
import { formatTime } from "../../util/util";
import Loader from "@/components/ui/loader";
import { useUser } from "@/redux/user";
import { useDaily } from "@/redux/daily";
import { useEffect, useId, useRef, useState } from "react";
import LeaguePracticeSelect from "./LeaguePracticeSelect";

const Title = ({ children }) => (
  <div className="flex items-center space-x-1 justify-end font-semibold">
    {children[0]}
    <div className="text-sm">{children[1]}</div>
  </div>
);

const Row = ({ children }) => (
  <div className="grid grid-cols-[76px_repeat(3,minmax(0,1fr))] items-center gap-1">
    {children.map((child, index) => (
      <div key={index} className="min-w-0 flex-1">
        {child}
      </div>
    ))}
  </div>
);

function CustomButton({ setShow }) {
  const { t: lobbyT } = useTranslation("lobby");

  function handleCustomClick(e) {
    showCustomGamePopup(true);
    setShow(false);
  }

  return (
    <button
      type="button"
      className="flex min-h-10 w-full items-center justify-center rounded-md bg-athens-gray-100 p-2 text-gray-700 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      onClick={handleCustomClick}
    >
      <div className="text-sm">{lobbyT("Custom")}</div>
    </button>
  );
}

function Buttons({ setShow }) {
  function handleClick() {
    setShow(false);
  }

  const { memberLoggedIn } = useUser();
  const { t } = useTranslation("common");
  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col space-y-2 pt-2">
        <Row>
          <Title>
            <BulletIcon />
            <div>Bullet</div>
          </Title>
          <GameButtonDropdown
            time={1}
            inc={0}
            variant={VARIANT_CHESS}
            onClick={handleClick}
          />
          <GameButtonDropdown
            time={2}
            inc={0}
            variant={VARIANT_CHESS}
            onClick={handleClick}
          />
          <GameButtonDropdown
            time={2}
            inc={1}
            variant={VARIANT_CHESS}
            onClick={handleClick}
          />
        </Row>
        <Row>
          <Title>
            <FaBolt />
            Blitz
          </Title>
          <GameButtonDropdown
            time={3}
            inc={0}
            variant={VARIANT_CHESS}
            onClick={handleClick}
          />
          <GameButtonDropdown
            time={5}
            inc={0}
            variant={VARIANT_CHESS}
            onClick={handleClick}
          />
          <GameButtonDropdown time={5} inc={2} onClick={handleClick} />
        </Row>
        <Row>
          <Title>
            <FaDragon />
            <div>Rapid</div>
          </Title>
          <GameButtonDropdown
            time={10}
            inc={0}
            variant={VARIANT_CHESS}
            onClick={handleClick}
          />
          <GameButtonDropdown
            time={15}
            inc={0}
            variant={VARIANT_CHESS}
            onClick={handleClick}
          />
          <GameButtonDropdown
            time={15}
            inc={5}
            variant={VARIANT_CHESS}
            onClick={handleClick}
          />
        </Row>
        {memberLoggedIn && (
          <Row>
            <Title>
              <FaPaperPlane />
              <div className="ml-0.5">{t("Daily")}</div>
            </Title>
            <GameButtonDropdown
              variant={VARIANT_CHESS}
              turnLength={1}
              onClick={handleClick}
            />
            <GameButtonDropdown
              variant={VARIANT_CHESS}
              turnLength={2}
              onClick={handleClick}
            />
            <GameButtonDropdown
              variant={VARIANT_CHESS}
              turnLength={3}
              onClick={handleClick}
            />
          </Row>
        )}
        <CustomButton setShow={setShow} />
      </div>
    </div>
  );
}

export default function ChooseGameDropdown({
  show,
  setShow,
  popover = false,
  showModeSelect = false,
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const choicesRef = useRef<HTMLDivElement>(null);
  const choicesId = useId();
  const [position, setPosition] = useState({ above: true, maxHeight: 320 });
  const { t: lobbyT } = useTranslation("lobby");

  useEffect(() => {
    if (!popover || !show) return;

    const positionChoices = () => {
      const bounds = triggerRef.current?.getBoundingClientRect();
      if (!bounds) return;
      // Leave the sticky mobile navigation clear and scroll the choices if needed.
      const spaceAbove = Math.max(0, bounds.top - 75);
      const spaceBelow = Math.max(0, window.innerHeight - bounds.bottom - 16);
      const above = spaceAbove >= 240 || spaceAbove >= spaceBelow;
      const maxHeight = above ? spaceAbove : spaceBelow;
      setPosition((current) =>
        current.above === above && current.maxHeight === maxHeight
          ? current
          : { above, maxHeight }
      );
    };
    const dismiss = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setShow(false);
    };

    positionChoices();
    const focusFrame = requestAnimationFrame(() => {
      const selected = choicesRef.current?.querySelector<HTMLButtonElement>(
        '[aria-pressed="true"]'
      );
      (
        selected ||
        choicesRef.current?.querySelector<HTMLButtonElement>("button")
      )?.focus({ preventScroll: true });
    });
    document.addEventListener("pointerdown", dismiss);
    window.addEventListener("resize", positionChoices);
    window.addEventListener("scroll", positionChoices, true);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("pointerdown", dismiss);
      window.removeEventListener("resize", positionChoices);
      window.removeEventListener("scroll", positionChoices, true);
    };
  }, [popover, show, setShow]);

  function selectAndClose(value: boolean) {
    setShow(value);
    if (popover && !value) triggerRef.current?.focus({ preventScroll: true });
  }
  const { time, inc, variant, turnLength, timeControl, modeId, gamesCreated } =
    useLobby();
  const { invites } = useDaily();
  const { id } = useUser();

  let isSentGame = !!gamesCreated.find(
    (game) => time == game.time && inc == game.inc && variant == game.variant
  );

  if (timeControl == TimeControl.DAILY) {
    invites.forEach((invite) => {
      if (
        id === invite.userId &&
        variant == invite.variantId &&
        turnLength == invite.turnLength
      ) {
        isSentGame = true;
      }
    });
  }

  const { t } = useTranslation("common");

  function renderInfo(show) {
    return (
      <div className="flex min-w-0 flex-1 items-center">
        <div className="flex min-w-0 w-full justify-between items-center pr-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <FaClock className="shrink-0" />
            <div
              className={`min-w-0 ${showModeSelect ? "" : "flex items-center"}`}
            >
              {modeId == ModeIds.DAILY ? (
                <span className="ml-0.5">
                  {t("days", { count: turnLength })}
                </span>
              ) : (
                formatTime(time, inc)
              )}
              {variant == VARIANT_CHESS960 && (
                <span
                  className={`text-xs font-normal text-altgray-600 ${
                    showModeSelect ? "block" : "ml-2"
                  }`}
                >
                  Chess960
                </span>
              )}
            </div>
          </div>
          {!show && isSentGame && <Loader color="blue" />}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="relative"
      onKeyDown={(event) => {
        if (popover && show && event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          selectAndClose(false);
        }
      }}
      onBlur={(event) => {
        if (
          popover &&
          show &&
          !event.currentTarget.contains(event.relatedTarget as Node)
        )
          setShow(false);
      }}
    >
      <div
        className={`grid w-full gap-2 ${
          showModeSelect ? "grid-cols-2" : "grid-cols-1"
        }`}
      >
        {showModeSelect && (
          <LeaguePracticeSelect onFocus={() => setShow(false)} />
        )}
        <button
          ref={triggerRef}
          type="button"
          aria-expanded={show}
          aria-haspopup={popover ? "dialog" : undefined}
          aria-controls={show ? choicesId : undefined}
          className="flex min-h-12 min-w-0 w-full cursor-pointer select-none items-center rounded-md bg-white p-3 text-left text-sm text-gray-700 active:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hover:bg-gray-50"
          onClick={() => {
            setShow(!show);
          }}
        >
          {renderInfo(show)}

          <div className="flex shrink-0 items-center">
            {show ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </button>
      </div>
      {show && (
        <div
          ref={choicesRef}
          id={choicesId}
          role={popover ? "dialog" : undefined}
          aria-label={popover ? lobbyT("Time") : undefined}
          className={
            popover
              ? `absolute -inset-x-3 z-40 overflow-y-auto overscroll-contain rounded-xl bg-gray-200 p-3 shadow-xl ring-1 ring-black/10 ${
                  position.above ? "bottom-full mb-2" : "top-full mt-2"
                }`
              : undefined
          }
          style={popover ? { maxHeight: position.maxHeight } : undefined}
        >
          <Buttons setShow={selectAndClose} />
        </div>
      )}
    </div>
  );
}
